<template>
  <div>
    <vx-card class="mt-base mb-5">
      <heading size="h2" style-size="h4" class="mb-5">{{ activityTitle }}</heading>
      <p class="mb-5">
        Person-centred care is where people, families, and communities are: Respected; Informed;
        Engaged; Supported; and treated with Dignity and Compassion.
      </p>

      <div class="vx-row mt-10">
        <div class="vx-col lg:w-2/3 md:w-2/3 w-full mb-base">
          <heading size="h3" style-size="h5" class="text-underline mb-5">Your Task:</heading>

          <p class="mb-5">
            Consider the 3 patients described below. They are all in hospital for an Appendectomy
            (to have their appendix surgically removed due to appendicitis).
          </p>
          <p class="mb-5">
            In what way could Person-Centred Care (outlined above) be important to them.
          </p>
          <p class="mb-5 text-underline_italic">Patient 1: An 8-year-old male child</p>
          <p class="mb-5">
            This 8-year-old boy is scared but thinks he has to be tough because his dad said boys
            have to be.
          </p>
          <p class="mb-5 text-underline_italic">Patient 2: A 19-year-old women</p>
          <p class="mb-5">
            This 19-year-old girl is more concerned with having a scar than an operation. It is all
            she seems focused on. She has no friends or family with her, and her next of kin is at
            work.
          </p>
          <p class="mb-5 text-underline_italic">Patient 3: A 35-year-old female immigrant</p>
          <p class="mb-5">
            This 35-year-old female immigrant only speaks some English. Her husband also only speaks
            a little English, but they have their 15-year-old daughter there who is fluent and often
            translates for her parents. The entire family trusts the doctors and nurses, but they
            are scared. The patient feels this has happened because she is being punished.
          </p>
          <p class="mb-5">Please tick one response for each question/scenario.</p>
          <p class="mb-5">
            You may also wish to use
            <a
              :href="'content/nurse/patient_and_family_centred_care.pdf' | blob_url"
              target="_blank"
              class="btn btn-primary btn-size-small"
              >this resource</a
            >
            to help you
          </p>

          <div v-for="(question, index) in questions" :key="index">
            <heading size="h3" style-size="h5" class="mb-5 questionlabel">
              {{ index }}. {{ question.question }}
            </heading>
            <p class="mb-2">You would:</p>
            <radio-buttons-wrapper
              v-model="data['question' + index]"
              :index="index + 1"
              :question="question"
              :is-editable="isEditable"
              show-question="false"
            />
          </div>

          <save-buttons :is-editable="isEditable" @save="save" />
        </div>
        <div class="vx-col lg:w-1/3 md:w-1/3 w-full mb-base pl-10">
          <img
            src="@/assets/images/experiences/nurse/pcc.png"
            class="img-responsive"
            alt="A person at the centre, surrounded by icons for the things person-centred care considers: ideas, communication, connectivity, thinking and wellbeing, medical care, medication, family and community, housing, relationships, trust, time, and location."
          />
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { successToast, errorToast } from '@/utils/toast';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import RadioButtonsWrapper from '@/views/WorkExperiences/components/ActivitiesBlocks/RadioButtonsWrapper';
import store from '@/store/store';
export default {
  components: { RadioButtonsWrapper },
  data() {
    return {
      login_type: localStorage.getItem('login_type'),
      questions: {
        1: {
          question:
            'This 8-year-old boy is scared but thinks he has to be tough because his dad said boys have to be.',
          answers: [
            {
              val: 1,
              answer:
                'a) Explain to the father that saying this to his son is inappropriate and guide the father on what to say to his son to fix this. Then, go on to use the hospital techniques that help children cope with pre-surgical fear and anxiety.',
              correct: false,
            },
            {
              val: 2,
              answer:
                'b) Talk to the patient in a warm friendly age appropriate manner and explain that even the tough children are still scared and it is OK to be both. You would then go on to use the hospital techniques that help children cope with pre-surgical fear and anxiety.',
              correct: true,
            },
            {
              val: 3,
              answer:
                'c) Approach the nurse in charge or the Dr and request they manage both the parents and the child. Then, go on to use the hospital techniques that help children cope with pre-surgical fear and anxiety.',
              correct: false,
            },
          ],
        },
        2: {
          question:
            'This 19-year-old girl is more concerned with having a scar than an operation. It is all she seems focussed on. She has no friends or family with her and her next of kin is at work.',
          answers: [
            {
              val: 4,
              answer:
                'a) Take into consideration that perhaps she doesn’t understand all that is going on since she is here alone. Take the time to go over everything with her again.',
              correct: false,
            },
            {
              val: 5,
              answer:
                'b) Explain to the patient that a scar is the last thing she should be concerned about. Explain it to her in a place away from others and ask her if she has any more important questions.',
              correct: false,
            },
            {
              val: 6,
              answer:
                'c) You can see this is causing the patient anxiety. You reassure the patient that any scar will be minimal and fade over time (if this is the prognosis) and that she will have the opportunity to discuss this further with the doctor. Explain also that you will provide her with post-care information that will help her with her concerns.',
              correct: true,
            },
          ],
        },
        3: {
          question:
            'This 35-year-old female immigrant only speaks a little English. Her husband also only speaks limited English as well, but they have their 15-year-old daughter there who is fluent and often translates for her parents. The entire family trusts the doctors and nurses, but they are scared. The patient feels she is in this situation because she is being punished.',
          answers: [
            {
              val: 7,
              answer:
                "a) Be very diligent in communicating clearly with both the patient and the daughter. Ask the patient and the daughter if they prefer an external translator. Attempt to get some background from the daughter as to why the mother feels she’s being punished, but be careful not to disregard the patient's feelings on the matter. Use warm, positive body language with the patient and her husband to soften the language barrier. Keep in mind that the daughter is not just a translator, but the patient's child who will also be scared for her mother and may also need support.",
              correct: false,
            },
            {
              val: 8,
              answer:
                "b) Explain to the daughter that she or her father needs to make her mother understand that she is being superstitious and that she is not being punished. Be very diligent in communicating clearly with both the patient and the daughter. Keep in mind that the daughter is not just a translator, but the patient's child who will also be scared for her mother and may also need support.",
              correct: false,
            },
            {
              val: 9,
              answer:
                "c) Respectfully organise a translator/interpreter, regardless of whether the daughter can translate, as she is still very young and may be worried about her mother and may also hold the same beliefs as her mother. This may not be helpful to the patient's treatment or recovery. An unbiased translator may be more reliable with relaying information between the Dr and patient as they will be impartial, accountable, and will be able to communicate clearly without bias between the family and medical team. Use warm, positive body language with the patient and her husband so that the language barrier is softened, and keep in mind that the daughter may need support in a different form to her parents.",
              correct: true,
            },
          ],
        },
      },
      answers_correct: {
        question1: true,
        question2: true,
        question3: true,
      },
      correct_answers: {
        question1: 2,
        question2: 6,
        question3: 9,
      },
    };
  },
  methods: {
    saveData() {
      if (this.validateAnswers()) this.save();
      else {
        this.$toast.error('Some of the answers are wrong, please try again.', errorToast());
      }
    },
    validateAnswers() {
      this.answers_correct.question1 = true;
      this.answers_correct.question2 = true;
      this.answers_correct.question3 = true;
      if (this.data.question1 != this.correct_answers.question1) {
        this.answers_correct.question1 = false;
      }
      if (this.data.question2 != this.correct_answers.question2) {
        this.answers_correct.question2 = false;
      }
      if (this.data.question3 != this.correct_answers.question3) {
        this.answers_correct.question3 = false;
      }
      if (
        !this.answers_correct.question1 ||
        !this.answers_correct.question2 ||
        !this.answers_correct.question3
      )
        return false;
      return true;
    },
    resetValidation() {
      this.answers_correct.question1 = true;
      this.answers_correct.question2 = true;
      this.answers_correct.question3 = true;
    },
    save() {
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    var vm = this;
    setTimeout(function () {
      vm.validateAnswers();
    }, 500);
  },
};
</script>

<style scoped>
.questionlabel {
  line-height: 1.5;
}
label.isbar_label span {
  color: #d40000;
  font-weight: bold;
}
.checkbox_answer {
  float: left;
  margin: 5px 10px 0 0;
}
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
</style>
